import React, { useState, useEffect } from "react";

import { Code, Play, Square, Terminal, ChevronUp, ChevronDown, Save } from "lucide-react";
import ScriptEditor from "./ScriptEditor";
import { useScriptExecutor } from "./ScriptExecutor";
import { useApiScripts } from "../../../../useQuery";

import { useNotification } from "../../../../hooks";
import { useQueryClient } from "@tanstack/react-query";

const Scripts = ({
  endpointId,
  testCaseId,
  requestContext,
  responseContext,
  onScriptResult,
  preRequestScript: externalPreRequestScript,
  postResponseScript: externalPostResponseScript,
  onPreRequestScriptChange,
  onPostResponseScriptChange,
  scriptLogs: externalScriptLogs,
  scriptResults: externalScriptResults,
  onClearLogs,
  endpointData,
  testCaseData,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [preRequestScript, setPreRequestScript] = useState("");
  const [postResponseScript, setPostResponseScript] = useState("");
  const [logs, setLogs] = useState([]);
  const [scriptResults, setScriptResults] = useState({
    preRequest: null,
    postResponse: null,
  });
  const [showConsole, setShowConsole] = useState(false);
  const notify = useNotification();
  const queryClient = useQueryClient();

  const currentScriptLogs = externalScriptLogs !== undefined ? externalScriptLogs : logs;
  const currentScriptResults =
    externalScriptResults !== undefined ? externalScriptResults : scriptResults;

  const currentPreRequestScript =
    externalPreRequestScript !== undefined ? externalPreRequestScript : preRequestScript;
  const currentPostResponseScript =
    externalPostResponseScript !== undefined ? externalPostResponseScript : postResponseScript;

  const updatePreRequestScript = (script) => {
    if (onPreRequestScriptChange) {
      onPreRequestScriptChange(script);
    } else {
      setPreRequestScript(script);
    }
  };

  const updatePostResponseScript = (script) => {
    if (onPostResponseScriptChange) {
      onPostResponseScriptChange(script);
    } else {
      setPostResponseScript(script);
    }
  };
  const { executeScript, terminateWorker } = useScriptExecutor();

  const { mutate: saveScript, isPending: isSavingScript } = useApiScripts({
    onSuccess: (data) => {
      notify("Script saved successfully", "success");
      queryClient.invalidateQueries(["latestAPIResults"]);
    },
    onError: (error) => {
      notify(error.message, "error");
    },
  });

  useEffect(() => {
    loadScripts();
  }, [endpointId, testCaseId, endpointData, testCaseData]);

  // Auto-open console when new logs arrive
  useEffect(() => {
    if (currentScriptLogs && currentScriptLogs.length > 0) {
      setShowConsole(true);
    }
  }, [currentScriptLogs.length]);

  const loadScripts = async () => {
    // If the parent component manages script state (via Context), do not overwrite it on mount.
    if (externalPreRequestScript !== undefined || externalPostResponseScript !== undefined) {
      return;
    }

    const data = testCaseData || endpointData;

    if (!data) {
      updatePreRequestScript("");
      updatePostResponseScript("");
      return;
    }

    let preRequestScriptContent = "";
    let postResponseScriptContent = "";

    if (data.preRequest) {
      preRequestScriptContent = data.preRequest;
    }
    if (data.postResponse) {
      postResponseScriptContent = data.postResponse;
    }

    updatePreRequestScript(preRequestScriptContent);
    updatePostResponseScript(postResponseScriptContent);
  };

  const saveScripts = async () => {
    if (!endpointId && !testCaseId) {
      console.error("Missing endpointId or testCaseId for saving scripts");
      return;
    }

    // Send both preRequest and postResponse content
    saveScript({
      endpointId: endpointId || null,
      testCaseId: testCaseId || null,
      preRequest: currentPreRequestScript,
      postResponse: currentPostResponseScript,
    });
  };

  const handleClearLogs = () => {
    setLogs([]);
  };

  const handleScriptChange = (type, content) => {
    if (type === "pre-request") {
      updatePreRequestScript(content);
    } else {
      updatePostResponseScript(content);
    }
  };

  const getCurrentScript = () => {
    return activeTab === 0 ? currentPreRequestScript : currentPostResponseScript;
  };

  const getScriptType = () => {
    return activeTab === 0 ? "pre-request" : "post-response";
  };

  const getScriptStatus = () => {
    const result =
      activeTab === 0 ? currentScriptResults.preRequest : currentScriptResults.postResponse;
    if (!result) return null;

    return {
      success: result.success,
      executionTime: result.executionTime,
      testResults: result.testResults || [],
    };
  };

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header and Tabs */}
      <div className="flex items-center justify-between px-4 py-0 border-b border-gray-200 bg-gray-50">
        <div className="flex">
          <button
            onClick={() => setActiveTab(0)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors cursor-pointer ${
              activeTab === 0
                ? "bg-blue-50 text-blue-700 border-b-2 border-blue-600"
                : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
            }`}
          >
            <Play size={14} />
            Pre-request
          </button>
          <button
            onClick={() => setActiveTab(1)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors cursor-pointer ${
              activeTab === 1
                ? "bg-blue-50 text-blue-700 border-b-2 border-blue-600"
                : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
            }`}
          >
            <Square size={14} />
            Post-response
          </button>
        </div>

        <div className="flex items-center gap-3">
          {getScriptStatus() && (
            <div className="flex items-center gap-1 mr-2" title="Last Execution Status">
              <div
                className={`w-2 h-2 rounded-full ${
                  getScriptStatus().success ? "bg-green-500" : "bg-red-500"
                }`}
              />
              <span className="text-xs text-gray-500 hidden sm:inline-block">
                {getScriptStatus().executionTime}ms
              </span>
            </div>
          )}

          <button
            onClick={() => setShowConsole(!showConsole)}
            className={`flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-md transition-colors cursor-pointer ${
              showConsole 
                ? "bg-gray-200 text-gray-800" 
                : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
            }`}
            title={showConsole ? "Hide Console" : "Show Console"}
          >
            <Terminal size={14} className={showConsole ? "text-gray-700" : "text-gray-500"} />
            Console
            {currentScriptLogs.length > 0 && (
              <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] leading-none ${
                showConsole ? "bg-gray-400 text-white" : "bg-gray-200 text-gray-700"
              }`}>
                {currentScriptLogs.length}
              </span>
            )}
          </button>

          <button
            onClick={saveScripts}
            disabled={isSavingScript || (!endpointId && !testCaseId)}
            className="flex items-center gap-2 px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-md transition-colors cursor-pointer"
            title={
              !endpointId && !testCaseId
                ? "Select an endpoint or test case to save scripts"
                : "Save both pre-request and post-response scripts"
            }
          >
            <Save size={14} />
            {isSavingScript ? "Saving..." : "Save Scripts"}
          </button>
        </div>
      </div>

      {/* Script Editor */}
      <div className="flex-1 min-h-0">
        <ScriptEditor
          scriptContent={getCurrentScript()}
          onChange={(content) => handleScriptChange(getScriptType(), content)}
          type={getScriptType()}
          logs={currentScriptLogs}
          onClearLogs={onClearLogs || handleClearLogs}
          showConsole={showConsole}
        />
      </div>

      {/* Compact Test Results */}
      {getScriptStatus()?.testResults && getScriptStatus().testResults.length > 0 && (
        <div className="border-t border-gray-200 p-3 bg-gray-50">
          <div className="flex items-center gap-2 mb-2">
            <h4 className="text-xs font-medium text-gray-700 uppercase tracking-wide">
              Test Results
            </h4>
            <div className="flex-1 h-px bg-gray-300"></div>
          </div>
          <div className="space-y-1">
            {getScriptStatus().testResults.map((test, index) => (
              <div
                key={index}
                className={`flex items-center gap-2 text-xs ${
                  test.status === "pass" ? "text-green-700" : "text-red-700"
                }`}
              >
                <span
                  className={`w-1.5 h-1.5 rounded-full ${
                    test.status === "pass" ? "bg-green-500" : "bg-red-500"
                  }`}
                />
                <span className="font-medium">{test.name}</span>
                {test.status === "fail" && test.error && (
                  <span className="text-red-600">({test.error})</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Scripts;
